import { useState } from 'react'
import { HttpVerb } from '../../../../../../types/api-types'
import { WellsAdminUserFieldsEdit } from '../../../../../../types/form-types'
import { FormDelegate } from '../../../../../../types/routing-types'
import { Permissions } from '../../../../../../types/user-types'
import ConfirmTransition from '../../../../../components/confirm-transition'
import { ERROR_MESSAGES } from '../../../../../components/errors/messages'
import LookupField from '../../../../../components/wells/lookup-field'
import WellEditButtons from '../../../../../components/wells/well-edit-buttons'
import { useGetWellData, useSaveWellData } from '../../../../../hooks/use-api'
import useEditForm from '../../../../../hooks/use-edit-form'
import useToastMessage from '../../../../../hooks/use-toast-message'
import useUser from '../../../../../hooks/use-user'
import { isValidCouncilEmail } from '../../../../../services/form-utils'
import { isCouncilUserOrAdmin, isSystemAdmin } from '../../../../../services/user-utils'
import { useParamsOr404 } from '../../../../../hooks/use-params-or-404'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  ButtonsContainer,
  CheckableField,
  CheckableFields,
  Divider,
  FormField,
  Heading,
  InlineNotification,
  Modal,
  ReadOnlyField,
  RecordContainer,
  RecordContent,
  RecordFooter,
  TextInput,
  Toggle,
  useTitleChange,
  WorkflowNav,
} from '@msaf/core-react'

function WellsUser({ setIsDirty }: FormDelegate) {
  useTitleChange('Administration | Edit user')
  const navigate = useNavigate()
  const { user } = useUser()
  const { id: userId } = useParamsOr404('id')
  const getAndUpdateUrl = `administration/user/${userId}/edit`
  const saveUrl = userId === 'new' ? `administration/user` : getAndUpdateUrl
  const cancelRoute = `/admin/users`

  const stateQueryResult = useGetWellData(getAndUpdateUrl)
  const saveMutationResult = useSaveWellData(saveUrl, userId === 'new' ? HttpVerb.POST : HttpVerb.PUT)

  const [isReviewerChangeConfirmationModalOpen, setIsReviewerChangeConfirmationModalOpen] = useState(false)
  const [showExternalApiUserWarning, setShowExternalApiUserWarning] = useState(false)

  const { fields, isLoading, error, isSaveError, setValue, handleSave, validationErrors, isSaved } =
    useEditForm<WellsAdminUserFieldsEdit>({
      stateQueryResult,
      saveMutationResult,
      setIsDirty,
    })

  const isNewUser = () => {
    return userId === 'new'
  }

  useToastMessage({
    errorMessage: validationErrors ? ERROR_MESSAGES.VALIDATION_ERROR : ERROR_MESSAGES.SAVE_FAILED,
    successMessage: isNewUser() ? 'User created.' : 'User details updated.',
    error,
    isSuccess: isSaved,
    isError: isSaveError,
  })

  const baseApiUserRole = Permissions.EXTERNAL_API_USER.split('.')[1]
  const isApiUser = () => {
    return !isNewUser() && baseApiUserRole === fields?.role
  }

  return (
    <RecordContainer
      workflowNav={<WorkflowNav sections={[{ links: [{ path: `/admin/users/${userId}/edit`, label: 'Details' }] }]} />}
    >
      <RecordContent>
        <ButtonsContainer containerStyle='left'>
          <Button
            onClick={() => navigate(`/admin/users`, { state: { activeTab: 2 } })}
            label='Back'
            iconAlignment='left'
            icon='arrow-left'
            buttonStyle='text-action'
          />
        </ButtonsContainer>
        <Heading level={2}>User details</Heading>
        <FormField
          isSkeleton={isLoading}
          label='User type'
          labelId='userType'
          validationMessages={validationErrors?.userType}
          isRequired={isNewUser()}
        >
          {isNewUser() ? (
            <CheckableFields ariaLabelledBy='userType' isHorizontal>
              <CheckableField
                isSkeleton={isLoading}
                name='councilStaff'
                id='Council staff'
                type='radio'
                label='Council staff'
                checked={fields.userType === 'Council staff'}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('userType', e.target.id)}
                describedBy='userType'
              />
              <CheckableField
                isSkeleton={isLoading}
                name='externalOrganisation'
                id='External organisation'
                type='radio'
                label='External organisation'
                checked={fields.userType === 'External organisation'}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('userType', e.target.id)}
                describedBy='userType'
              />
            </CheckableFields>
          ) : (
            <ReadOnlyField isSkeleton={isLoading} labelledBy='userType' value={`${fields.userType ?? ''}`} />
          )}
        </FormField>
        {fields.userType !== 'Public' && (
          <FormField
            isSkeleton={isLoading}
            label='Organisation'
            labelId='organisation'
            validationMessages={validationErrors?.organisation}
            isRequired={isNewUser()}
          >
            <LookupField
              isSkeleton={isLoading}
              fieldId='organisation'
              labelledBy='organisation'
              value={fields.organisation ?? ''}
              setValue={({ value }) => setValue('organisation', value)}
              fieldFilters={{ isExternal: fields.userType === 'External organisation' }}
              isDisabled={!isNewUser()}
              isInvalid={!!validationErrors.organisation}
            />
          </FormField>
        )}
        {fields.userType === 'External organisation' && isSystemAdmin(user) && (
          <FormField
            isSkeleton={isLoading}
            label='Council'
            labelId='organisationCouncil'
            validationMessages={validationErrors?.organisationCouncil}
            isRequired={isNewUser()}
          >
            <LookupField
              isSkeleton={isLoading}
              fieldId='organisationCouncil'
              labelledBy='organisationCouncil'
              value={fields.organisationCouncil ?? ''}
              setValue={({ value }) => setValue('organisationCouncil', value)}
              isDisabled={!isNewUser()}
              fieldFilters={{ organisation: fields.organisation ?? '' }}
              isInvalid={!!validationErrors.organisationCouncil}
            />
          </FormField>
        )}
        <FormField
          isSkeleton={isLoading}
          label='First name'
          htmlFor='firstName'
          validationMessages={validationErrors?.firstName}
          isRequired
        >
          <TextInput
            isSkeleton={isLoading}
            id='firstName'
            value={fields.firstName ?? ''}
            setValue={(value: string) => setValue('firstName', value)}
            isInvalid={!!validationErrors.firstName}
            isDisabled={isApiUser()}
          />
        </FormField>
        <FormField
          isSkeleton={isLoading}
          label='Last name'
          htmlFor='lastName'
          validationMessages={validationErrors?.lastName}
          isRequired
        >
          <TextInput
            isSkeleton={isLoading}
            id='lastName'
            value={fields.lastName ?? ''}
            setValue={(value: string) => setValue('lastName', value)}
            isInvalid={!!validationErrors.lastName}
            isDisabled={isApiUser()}
          />
        </FormField>
        <FormField
          isSkeleton={isLoading}
          label='Email'
          htmlFor='email'
          validationMessages={validationErrors?.email}
          isRequired
        >
          <TextInput
            isSkeleton={isLoading}
            id='email'
            value={fields.email ?? ''}
            setValue={(value: string) => setValue('email', value)}
            isInvalid={!!validationErrors.email}
            isDisabled={isApiUser()}
          />
        </FormField>
        {fields.email && fields.userType === 'Council staff' && !isValidCouncilEmail(fields.email) && (
          <div className='c-input-container--default'>
            <InlineNotification isDismissable={false} messageType='warning'>
              The provided email may not be registered with any council.
            </InlineNotification>
          </div>
        )}
        <FormField isSkeleton={isLoading} label='Contact number' htmlFor='contactNumber'>
          <TextInput
            isSkeleton={isLoading}
            id='contactNumber'
            value={fields.contactNumber ?? ''}
            setValue={(value: string) => setValue('contactNumber', value)}
            isDisabled={isApiUser()}
          />
        </FormField>
        <Divider />
        <Heading isSkeleton={isLoading} level={2}>
          User permissions
        </Heading>
        {fields.userType !== 'Public' && fields.role !== 'authenticated_public' && (
          <>
            <FormField
              isSkeleton={isLoading}
              label='Role'
              labelId='role'
              validationMessages={validationErrors?.role}
              isRequired
            >
              <LookupField
                isSkeleton={isLoading}
                fieldId={fields.userType === 'External organisation' ? 'companyContactRole' : 'councilRole'}
                labelledBy='role'
                value={fields.role ?? ''}
                setValue={({ value }) => setValue('role', value)}
                isInvalid={!!validationErrors.role}
                fieldFilters={isApiUser() ? {} : { roleFilter: baseApiUserRole }}
                isDisabled={isApiUser()}
                handleChangeCallback={({ label }) => setShowExternalApiUserWarning(label === 'External Api User')}
              />
            </FormField>
            {showExternalApiUserWarning && (
              <InlineNotification isDismissable={false} messageType='warning'>
                An existing user cannot be assigned to a role of external api user.
              </InlineNotification>
            )}
          </>
        )}

        {isApiUser() && isSystemAdmin(user) && (
          <>
            <FormField isSkeleton={isLoading} labelId='apiToken' label='API Token'>
              <ReadOnlyField isSkeleton={isLoading} labelledBy='apiToken' value={fields.apiToken} />
            </FormField>
            <FormField isSkeleton={isLoading} label='Refresh API Token' htmlFor='refreshToken'>
              <Toggle
                id='refreshTokenId'
                isChecked={fields?.refreshToken}
                isSkeleton={isLoading}
                onChange={() => setValue('refreshToken', !fields.refreshToken)}
                name='refreshToken'
              />
            </FormField>
          </>
        )}
        {!isNewUser() && (
          <>
            {userId === user?.id || fields.isPrimaryContact ? (
              <>
                <FormField isSkeleton={isLoading} labelId='status' label='Status'>
                  <ReadOnlyField
                    isSkeleton={isLoading}
                    labelledBy='status'
                    value={fields.isActive ? 'Active' : 'Archived'}
                  />
                </FormField>
                <div className='c-input-container--default'>
                  <InlineNotification isDismissable={false} messageType='info'>
                    Primary contacts or currently logged-in user cannot be archived.
                  </InlineNotification>
                </div>
              </>
            ) : (
              <>
                <FormField isSkeleton={isLoading} label='Status' htmlFor='status'>
                  <Toggle
                    id='status'
                    isChecked={fields.isActive}
                    isSkeleton={isLoading}
                    onChange={() => setValue('isActive', !fields.isActive)}
                    name='status'
                  >
                    {fields.isActive ? 'Active' : 'Archived'}
                  </Toggle>
                </FormField>
                {!fields.isActive && (
                  <div className='c-input-container--default'>
                    <InlineNotification isDismissable={false} messageType='warning'>
                      Archived user will no longer be able to access the system.
                    </InlineNotification>
                  </div>
                )}
              </>
            )}
          </>
        )}
        {fields.userType === 'Council staff' && isCouncilUserOrAdmin(user) && !isApiUser() && (
          <>
            <FormField isSkeleton={isLoading} label='' htmlFor='isPrimaryReviewer'>
              <Toggle
                id='isPrimaryReviewer'
                isChecked={fields.isPrimaryReviewer === true}
                isSkeleton={isLoading}
                isDisabled={!fields.isActive && !isNewUser()}
                onChange={() => {
                  setValue('isPrimaryReviewer', !fields.isPrimaryReviewer)
                  setIsReviewerChangeConfirmationModalOpen(true)
                }}
                name='isPrimaryReviewer'
              >
                Primary reviewer
              </Toggle>
            </FormField>
            {!fields.isActive && !isNewUser() && (
              <div className='c-input-container--default'>
                <InlineNotification isDismissable={false} messageType='warning'>
                  A primary reviewer cannot be an archived user
                </InlineNotification>
              </div>
            )}
          </>
        )}
      </RecordContent>
      <RecordFooter>
        <WellEditButtons
          actions={[
            {
              label: 'Save',
              isDisabled: showExternalApiUserWarning,
              buttonStyle: 'primary',
              onClick: () => {
                handleSave(async (id, refetch) => {
                  const postSaveUrl = isNewUser() ? cancelRoute : `/admin/users/${id}/edit`
                  await refetch?.()
                  navigate(postSaveUrl, {
                    state: {
                      isProgrammaticTransition: true,
                    },
                  })
                })
              },
            },
            {
              label: 'Return to view',
              buttonStyle: 'text-action',
              onClick: () => navigate(cancelRoute),
            },
          ]}
        />
      </RecordFooter>
      <Modal
        isOpen={fields?.isPrimaryReviewer && isReviewerChangeConfirmationModalOpen ? true : false}
        onRequestClose={() => {
          setIsReviewerChangeConfirmationModalOpen(false)
          setValue('isPrimaryReviewer', false)
        }}
        heading='Change primary reviewer?'
        bodyText='Are you sure you want to change the primary reviewer for this organisation?'
        contentLabel='Primary reviewer changed confirmation modal'
        buttons={
          <>
            <Button label='Confirm' onClick={() => setIsReviewerChangeConfirmationModalOpen(false)} />
            <Button label='Cancel' buttonStyle='secondary' onClick={() => setValue('isPrimaryReviewer', false)} />
          </>
        }
      />
    </RecordContainer>
  )
}

export default function EditWellsUser() {
  const [isDirty, setIsDirty] = useState<boolean>(false)
  return (
    <ConfirmTransition isFormDirty={isDirty}>
      <WellsUser setIsDirty={setIsDirty} />
    </ConfirmTransition>
  )
}
